import { html, type TemplateResult } from 'lit';
import type { CatalogDownloadProgress } from '../../../domain/ports/CatalogDownloadPort.ts';
import { formatBytesEs } from './syncUiHelpers.ts';

export function catalogDownloadHasVisibleProgress(progress: CatalogDownloadProgress | null): boolean {
  return progress != null && progress.loaded > 0 && (progress.phase === 'applying' ? progress.total > 0 : true);
}

export function catalogDownloadProgressPercent(progress: CatalogDownloadProgress): number | null {
  if (progress.total <= 0) return null;
  return Math.min(100, Math.round((progress.loaded / progress.total) * 100));
}

export function catalogDownloadProgressDetail(progress: CatalogDownloadProgress): string {
  const pct = catalogDownloadProgressPercent(progress);
  if (progress.phase === 'applying') {
    return pct == null ? `${progress.loaded} registros` : `${pct}% (${progress.loaded} / ${progress.total} registros)`;
  }
  if (pct == null) return formatBytesEs(progress.loaded);
  return `${pct}% (${formatBytesEs(progress.loaded)} / ${formatBytesEs(progress.total)})`;
}

export function catalogDownloadProgressLabel(progress: CatalogDownloadProgress): string {
  return progress.phase === 'downloading' ? 'Descargando catálogos' : 'Aplicando catálogos';
}

export function catalogDownloadProgressBarTemplate(progress: CatalogDownloadProgress): TemplateResult {
  const pct = catalogDownloadProgressPercent(progress);
  const detail = catalogDownloadProgressDetail(progress);
  const label = catalogDownloadProgressLabel(progress);
  const barWidth = pct ?? 100;
  const indeterminate = pct == null;

  return html`
    <div class="mt-2" role="status" aria-live="polite">
      <span class="text-[0.65rem] font-semibold uppercase tracking-wide text-[color:var(--md-sys-color-primary)]"
        >${label}</span
      >
      <div
        class="mt-1 h-2 w-full overflow-hidden rounded-full bg-[color:var(--md-sys-color-surface-container-highest)]"
        role="progressbar"
        aria-valuemin="0"
        aria-valuemax="100"
        aria-valuenow=${pct ?? undefined}
        aria-valuetext=${detail}
      >
        <div
          class="h-full bg-[color:var(--md-sys-color-primary)] transition-all duration-300 ${indeterminate
            ? 'animate-pulse'
            : ''}"
          style="width: ${indeterminate ? '100%' : `${barWidth}%`}; opacity: ${indeterminate ? '0.45' : '1'}"
        ></div>
      </div>
      <p class="mt-1 text-right text-xs tabular-nums text-[color:var(--md-sys-color-on-surface-variant)]">${detail}</p>
    </div>
  `;
}
